import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/** @jsxRuntime automatic */
/** @jsxImportSource react */
/**
 * StarScribe's shell in the widget (docs/CONTRACT-starscribe-clients.md sections 5 to 7): one hook the core calls through its
 * parts (assistantParts.ts; the web entry's is inert, starscribe/inert.ts), so GyroAssistant.tsx only places what it hands
 * back:
 *  - the "StarScribe" item in the menu (picture 34) and the list it opens in the main area (StarScribeList.tsx);
 *  - "Edit in StarScribe ▾" first in an answer's row (picture 31, EditInStarScribe.tsx);
 *  - the page cards under answers (picture 33, PageCard.tsx), with the D12 auto-open of a page Gyro made;
 *  - the side panel's StarScribe tab, or the narrow tabs over the chat where the panel cannot show (tab.tsx);
 *  - the ask field `starscribe: { pageId }` while a page is open beside the chat (section 5);
 *  - in the desktop app, a page's own window (picture 13) and the asks made there.
 * All of it only where section 7 allows: the desktop app, Gyro on the web or the Office pane, and GET /v1/starscribe/me
 * says enabled. Otherwise none of it renders, and nothing loads.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NotebookPen } from '../starscribeIcons.js';
import { EditInStarScribe } from './EditInStarScribe.js';
import { STARSCRIBE_DESKTOP_RATES } from './lazy.js';
import { PageCard } from './PageCard.js';
import { answeredOff, parseStarscribeAsk, parseStarscribeMe, starscribeMeNextAsk, } from './pages.js';
import { StarScribeList } from './StarScribeList.js';
import { STARSCRIBE_POPOUT_ASK_EVENT, STARSCRIBE_POPOUT_RESULT_EVENT, STARSCRIBE_TAB_ID, StarScribeNarrowPage, StarScribeNarrowTabs, StarScribePopOut, StarScribeTabBody, StarScribeTabHeader, payloadPageId, } from './tab.js';
import { starscribeApiFor } from './transport.js';
import { shortPageTitle } from './words.js';
/** The surface a widget is: the desktop app, Gyro on the web (the `web` pane) or the Office pane; null anywhere else. */
export function starscribeSurface(options) {
    if (options.desktop && options.appKey === 'desktop')
        return 'desktop';
    if (options.layout === 'pane' && options.appKey === 'web')
        return 'web';
    if (options.layout === 'pane' && options.appKey === 'office')
        return 'office';
    return null;
}
/** What is said under the page's header when the host's Open larger fails: its own sentence, else a plain one. */
export function openLargerFailure(failure) {
    const message = failure instanceof Error ? failure.message.trim() : '';
    return message ? message.slice(0, 300) : 'The page could not open in a larger window. Try again.';
}
/** The answers whose cards are kept, newest last. */
const MAX_CARD_REPLIES = 200;
/**
 * Below this width of the page's header in the panel's head, the page is drawn compact (its header's buttons as icons), as in
 * a narrow window: the labels need about this much beside the title, and the panel's own Full screen and Close stay clear.
 */
export const PANEL_COMPACT_BELOW = 400;
function freshState(owner) {
    return { owner, listOpen: false, narrowView: 'page', cards: new Map(), titles: new Map(), poppedOut: new Set() };
}
function withTitle(titles, pageId, title) {
    if (!title || titles.get(pageId) === title)
        return titles;
    return new Map(titles).set(pageId, title);
}
export function useStarscribe(options) {
    const { client, userKey, onScreen, sidePanel, wide, theme, you, host, mascotSrc } = options;
    const surface = starscribeSurface(options);
    const request = useMemo(() => (typeof client.starscribeRequest === 'function' ? client.starscribeRequest.bind(client) : null), [client]);
    // Section 7: /me, per person and surface, while Gyro is on screen. "On" is kept. "Off" (the service's own no, a 403 or a
    // 404) is asked again when Gyro next comes on screen, at most every 10 minutes, so a switch turned on reaches an open
    // widget. A /me that got no answer (a 401 such as the desktop agent's own "sign in", a 429, a 5xx, a dropped connection)
    // is never taken for "off": it is tried again while Gyro stays on screen (15 s, doubling, at most 5 minutes) and when it
    // next comes on screen. Kept as "off", such a failure hid StarScribe until the page reloaded: in the desktop app, until
    // the next logon.
    const meKey = surface && request ? `${surface}:${userKey}` : '';
    const [me, setMe] = useState(null);
    useEffect(() => {
        if (!meKey || !request || !onScreen)
            return undefined;
        const known = me?.key === meKey ? me : null;
        const askIn = starscribeMeNextAsk(known, Date.now());
        if (askIn === null)
            return undefined;
        const controller = new AbortController();
        const ask = () => {
            request('/v1/starscribe/me', { signal: controller.signal }).then((raw) => { if (!controller.signal.aborted)
                setMe({ key: meKey, enabled: parseStarscribeMe(raw).enabled, atMs: Date.now(), failures: 0 }); }, (failure) => {
                if (controller.signal.aborted)
                    return;
                setMe({ key: meKey, enabled: false, atMs: Date.now(), failures: answeredOff(failure) ? 0 : (known?.failures ?? 0) + 1 });
            });
        };
        const timer = askIn > 0 ? setTimeout(ask, askIn) : null;
        if (!timer)
            ask();
        return () => { if (timer)
            clearTimeout(timer); controller.abort(); };
    }, [meKey, request, onScreen, me]);
    const on = Boolean(meKey && request && me?.key === meKey && me.enabled);
    const [stored, setStored] = useState(() => freshState(userKey));
    const state = stored.owner === userKey ? stored : freshState(userKey);
    const ownerRef = useRef(userKey);
    ownerRef.current = userKey;
    const update = useCallback((change) => {
        setStored((previous) => change(previous.owner === ownerRef.current ? previous : freshState(ownerRef.current)));
    }, []);
    // The panel's own calls, read when used (its API object changes with the open tab).
    const panelRef = useRef(sidePanel);
    panelRef.current = sidePanel;
    const onRef = useRef(on);
    onRef.current = on;
    const openPageId = () => (panelRef.current.activeTabId === STARSCRIBE_TAB_ID ? payloadPageId(panelRef.current.payloadFor(STARSCRIBE_TAB_ID)) : null);
    /** A page beside the chat: the panel's StarScribe tab where the panel shows, else the narrow "Chat | <title>" view. */
    const openPage = useCallback((pageId, title) => {
        update((current) => {
            const poppedOut = current.poppedOut.has(pageId) ? new Set([...current.poppedOut].filter((id) => id !== pageId)) : current.poppedOut;
            return { ...current, listOpen: false, narrowView: 'page', poppedOut, titles: title ? withTitle(current.titles, pageId, title) : current.titles };
        });
        panelRef.current.open(STARSCRIBE_TAB_ID, { pageId });
    }, [update]);
    const closePage = useCallback(() => {
        if (panelRef.current.activeTabId === STARSCRIBE_TAB_ID)
            panelRef.current.close();
    }, []);
    const addCard = useCallback((replyKey, card) => update((current) => {
        const cards = new Map(current.cards);
        cards.delete(replyKey);
        cards.set(replyKey, [...(current.cards.get(replyKey) ?? []).filter((existing) => existing.pageId !== card.pageId), card]);
        while (cards.size > MAX_CARD_REPLIES)
            cards.delete(cards.keys().next().value);
        return { ...current, cards, titles: withTitle(current.titles, card.pageId, card.title) };
    }), [update]);
    // The pages an answer made come once, with its result: a card each, and the last one marked open beside the chat (D12).
    const seenRef = useRef(new Set());
    const answerPages = useCallback((replyKey, pages, viewed) => {
        let toOpen = null;
        for (const page of pages) {
            const seen = `${ownerRef.current}\n${replyKey}\n${page.pageId}`;
            if (seenRef.current.has(seen))
                continue;
            seenRef.current.add(seen);
            if (seenRef.current.size > 1000)
                seenRef.current.delete(seenRef.current.values().next().value);
            addCard(replyKey, { pageId: page.pageId, title: page.title, version: 1, atMs: Date.now() });
            if (page.open)
                toOpen = page;
        }
        if (toOpen && viewed && onRef.current)
            openPage(toOpen.pageId, toOpen.title);
    }, [addCard, openPage]);
    // The desktop app: a page's own window (picture 13). Its answer says whether the window opened (or came to the front),
    // and only then does the page leave the panel; a refusal keeps it there.
    useEffect(() => {
        if (surface !== 'desktop' || !on)
            return undefined;
        const result = (event) => {
            const detail = event.detail;
            const pageId = payloadPageId(detail);
            if (!pageId || (detail?.status !== 'opened' && detail?.status !== 'focused'))
                return;
            update((current) => ({ ...current, poppedOut: new Set(current.poppedOut).add(pageId) }));
            if (openPageId() === pageId)
                panelRef.current.close();
        };
        window.addEventListener(STARSCRIBE_POPOUT_RESULT_EVENT, result);
        return () => window.removeEventListener(STARSCRIBE_POPOUT_RESULT_EVENT, result);
    }, [surface, on, update]);
    // Asks made on a page in its own window come here, to be sent in this chat: the desktop app's page windows, and the Office
    // pane's larger window (its Open larger dialog), whose host hands them on as the same event.
    const askRef = useRef(options.ask);
    askRef.current = options.ask;
    useEffect(() => {
        if ((surface !== 'desktop' && surface !== 'office') || !on)
            return undefined;
        const asked = (event) => {
            const ask = parseStarscribeAsk(event.detail);
            if (ask)
                askRef.current(ask);
        };
        window.addEventListener(STARSCRIBE_POPOUT_ASK_EVENT, asked);
        return () => window.removeEventListener(STARSCRIBE_POPOUT_ASK_EVENT, asked);
    }, [surface, on]);
    // The page open beside the chat: the panel's StarScribe tab and its payload, in the wide layout and the narrow one alike.
    const openId = on && sidePanel.activeTabId === STARSCRIBE_TAB_ID ? payloadPageId(sidePanel.payloadFor(STARSCRIBE_TAB_ID)) : null;
    const page = openId ? { pageId: openId, title: state.titles.get(openId) ?? '' } : null;
    // An ask from the page shows the chat it goes to when the list covers it.
    const pageAsk = useCallback((input) => {
        update((current) => (current.listOpen ? { ...current, listOpen: false } : current));
        askRef.current(input);
    }, [update]);
    // One API per client (transport.ts): the page and its header share it, and with it one connection to the page.
    const api = useMemo(() => starscribeApiFor(client), [client]);
    // The room the page's header has in the panel's head (StarScribeTabHeader measures it): a narrow panel draws the page compact.
    const [headWidth, setHeadWidth] = useState(0);
    const panelCompact = headWidth > 0 && headWidth < PANEL_COMPACT_BELOW;
    // The host's Open larger (the Office pane's dialog) may answer with a promise: a failure is said under the page's header.
    const [largerFailed, setLargerFailed] = useState(null);
    const openLarger = (target) => {
        setLargerFailed(null);
        const failed = (failure) => setLargerFailed({ pageId: target.pageId, message: openLargerFailure(failure) });
        try {
            const done = host?.openLarger?.(target);
            if (typeof done === 'object' && done !== null && typeof done.then === 'function')
                done.then(undefined, failed);
        }
        catch (failure) {
            failed(failure);
        }
    };
    const view = page ? {
        api,
        pageId: page.pageId,
        ask: pageAsk,
        theme,
        you,
        onClose: closePage,
        ...(panelCompact ? { compact: true } : {}),
        ...(mascotSrc ? { mascotSrc } : {}),
        ...(surface === 'desktop' ? { rates: STARSCRIBE_DESKTOP_RATES } : {}),
        ...(host?.insert ? { insert: host.insert } : {}),
        ...(host?.openLarger ? { openLarger: () => openLarger({ pageId: page.pageId, title: page.title }) } : {}),
    } : null;
    // Pop out is the desktop app's (its window host answers it); nowhere else.
    const popOut = surface === 'desktop';
    // The panel's tab, while StarScribe is on: its header and body read the latest state when the panel draws them.
    const tabRef = useRef({ view, title: page?.title ?? '', popOut });
    tabRef.current = { view, title: page?.title ?? '', popOut };
    const register = sidePanel.register;
    useEffect(() => {
        if (!on)
            return undefined;
        return register({
            id: STARSCRIBE_TAB_ID,
            label: 'StarScribe',
            icon: _jsx(NotebookPen, { size: 14, "aria-hidden": "true" }),
            headerExtras: () => _jsx(StarScribeTabHeader, { view: tabRef.current.view, title: tabRef.current.title, popOut: tabRef.current.popOut, onWidth: setHeadWidth }),
            render: () => _jsx(StarScribeTabBody, { view: tabRef.current.view }),
        });
    }, [on, register]);
    const narrow = Boolean(on && !wide && page && !state.listOpen);
    const main = !on || !request ? null
        : state.listOpen ? _jsx(StarScribeList, { request: request, you: you, onOpen: (row) => openPage(row.pageId, row.title) }, userKey)
            : narrow && state.narrowView === 'page' && view && page ? _jsx(StarScribeNarrowPage, { view: view, title: page.title, notice: largerFailed?.pageId === page.pageId ? largerFailed.message : undefined })
                : null;
    const strip = narrow && page ? _jsx(StarScribeNarrowTabs, { label: surface === 'office' ? 'Page' : shortPageTitle(page.title), title: page.title, view: state.narrowView, onView: (next) => update((current) => ({ ...current, narrowView: next })), onClose: closePage, popOut: popOut ? _jsx(StarScribePopOut, { pageId: page.pageId, title: page.title }) : null }) : null;
    return {
        on,
        page,
        listOpen: on && state.listOpen,
        openList: () => update((current) => (current.listOpen ? current : { ...current, listOpen: true })),
        closeList: () => update((current) => (current.listOpen ? { ...current, listOpen: false } : current)),
        main,
        strip,
        askField: (override) => (override ? { starscribe: override } : page ? { starscribe: { pageId: page.pageId } } : {}),
        answerPages,
        editButton: (replyKey, answer, conversationId) => (on && request && answer.trim()
            ? _jsx(EditInStarScribe, { request: request, answer: answer, conversationId: conversationId, onPage: (result) => { addCard(replyKey, { ...result, atMs: Date.now() }); openPage(result.pageId, result.title); } })
            : null),
        cards: (replyKey) => {
            const list = on ? state.cards.get(replyKey) : undefined;
            if (!list?.length)
                return null;
            const nowMs = Date.now();
            return _jsx("div", { className: "gyro-ss-cards", children: list.map((card) => _jsx(PageCard, { card: card, nowMs: nowMs, poppedOut: state.poppedOut.has(card.pageId), onOpen: (open) => openPage(open.pageId, open.title) }, card.pageId)) });
        },
        navItem: (active, onOpen) => (on
            ? _jsxs("button", { type: "button", className: `gyro-desktop-nav-item${active ? ' active' : ''}`, onClick: onOpen, "aria-current": active ? 'page' : undefined, children: [_jsx(NotebookPen, { size: 17 }), _jsx("span", { children: "StarScribe" })] })
            : null),
    };
}
/** The full entry's shell (fullParts.ts). */
export const STARSCRIBE_SHELL = { useStarscribe };
//# sourceMappingURL=shell.js.map